import { Footer } from "~/components/Footer";
import { PageWrapper, SidebarLayout } from "~/components/SidebarLayout";
import { Navbar } from "~/components/Navbar/component";
import { UserFetcher } from "~/modules/user/components/UserFetcher";
import { NextSeo } from "next-seo";
import { PageTrack } from "~/modules/analytics/components/PageTrack";
import { Routes } from "~/routes";
import { Asset, AssetColumns } from "~/modules/assets/types";
import {
    FilterOperators,
    QueryResponse,
    SortDirections,
    Filter,
    FilterMetadata,
} from "~/modules/filters/types";
import {
    LeagueTableGroupTypes,
    LeagueTableTabsByDistributionType,
} from "~/modules/league_tables/types";
import { NEXTJS_REVALIDATE_TIMEOUT } from "~/server/consts";
import { addBaseUrl } from "~/modules/app/seo/addBaseUrl";
import { AssetClassPageSEO } from "~/modules/app/seo/AssetClassPageSEO";
import { BreadcrumbSEO } from "~/modules/app/seo/BreadcrumbSEO";
import {
    ASSET_CLASS_IDS,
    ASSET_TYPES,
    DISTRIBUTION_TYPE_AGGREGATE_TOOLTIP_LINK_TO,
    DISTRIBUTION_TYPE_TOOLTIPS,
    MEASURE_IDS,
    MEASURE_SLUGS,
    SOCIAL_OG_IMAGE_URL,
    TOKENIZATION_TYPE_IDS,
} from "~/consts";
import { AssetClassIndividualPage } from "~/modules/asset_classes/pages/individual";
import {
    ASSET_CLASS_DESCRIPTIONS,
    ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS,
    ASSET_CLASS_PAGES_TO_FILTER,
    ASSET_CLASS_PAGE_TO_SORT,
} from "~/modules/asset_classes/consts";
import {
    TokenTimeseriesGroupBy,
    TokenTimeseriesColumns,
    TimeSeriesItem,
} from "~/modules/token_timeseries/types";
import { TimeseriesSegmentSelectProps } from "~/modules/visualization/components/Timeseries/types";
import { Props as AggregateDisplayProps } from "~/components/AggregateDisplay";
import { queryTokenAggregateValueWith30D } from "~/modules/token_timeseries/api/queryTokenAggregateValue";
import { AggregateValueTypes } from "~/components/AggregateDisplay/types";
import { queryAssetFilterMeta, queryAssets } from "~/modules/assets/api/router";
import { getTimeseriesModes } from "~/modules/visualization/components/Timeseries/getTimeseriesModes";
import { ColumnMetadata } from "~/components/DataGrid";
import { AssetTableRowData } from "~/modules/assets/components/AssetTable/types";
import { ASSET_NAME_AND_TICKER_COLUMN_EXPANDABLE } from "~/modules/assets/components/AssetTable/columns/AssetName";
import { BRIDGED_TOKEN_VALUE_DOLLAR_COLUMN } from "~/modules/assets/components/AssetTable/columns/BridgedTokenValueDollar";
import { NET_ASSET_VALUE_COLUMN } from "~/modules/assets/components/AssetTable/columns/NetAssetValue";
import {
    PERFORMANCE_1_MONTH_COLUMN,
    PERFORMANCE_1_YEAR_COLUMN,
} from "~/modules/assets/components/AssetTable/columns/Performance";
import { HOLDING_ADDRESS_COUNT_COLUMN } from "~/modules/assets/components/AssetTable/columns/Holders";
import { UNDERLYING_ASSET_COLUMN } from "~/modules/assets/components/AssetTable/columns/UnderlyingAssets";
import { TOKENIZATION_TYPE_COLUMN } from "~/modules/assets/components/AssetTable/columns/TokenizationType";
import { PLATFORM_COLUMN } from "~/modules/assets/components/AssetTable/columns/Platform";
import { NETWORKS_COLUMN } from "~/modules/assets/components/AssetTable/columns/Networks";
import { BRIDGED_TOKEN_VALUE_DOLLAR_SPARKLINE_COLUMN } from "~/modules/assets/components/AssetTable/columns/Sparklines";
import { TRAILING_30_DAY_TRANSFER_VOLUME_COLUMN } from "~/modules/assets/components/AssetTable/columns/TransferVolume";
import { INCORPORATION_COUNTRY_COLUMN } from "~/modules/assets/components/AssetTable/columns/IncorporationCountry";
import { LICENSE_OR_REGULATORY_FRAMEWORK } from "~/modules/assets/components/AssetTable/columns/RegulatoryFramework";
import {
    PRIMARY_MARKET_SUBSCRIPTION_FEE_COLUMN,
    PRIMARY_MARKET_REDEMPTION_FEE_COLUMN,
} from "~/modules/assets/components/AssetTable/columns/PrimaryMarket";
import { TRAILING_30_DAY_TRANSFER_COUNT_COLUMN } from "~/modules/assets/components/AssetTable/columns/TransferCount";
import { TRAILING_30_DAY_ACTIVE_ADDRESSES_COUNT_COLUMN } from "~/modules/assets/components/AssetTable/columns/ActiveAddresses";
import { CONTRACT_ADDRESS_COLUMN } from "~/modules/assets/components/AssetTable/columns/ContractAddress";
import { SEARCH_FILTER_METADATA } from "~/modules/filters/consts";
import { queryLeagueTablesByDistributionType } from "~/modules/league_tables/api/queryLeagueTablesByDistributionType";

// // // //

const SEO_TITLE = "Tokenized Stocks";
const SEO_DESCRIPTION = ASSET_CLASS_DESCRIPTIONS.stocks;

const STOCKS_TABLE_COLUMNS: ColumnMetadata<AssetTableRowData, AssetColumns>[] =
    [
        ASSET_NAME_AND_TICKER_COLUMN_EXPANDABLE,
        BRIDGED_TOKEN_VALUE_DOLLAR_COLUMN,
        NET_ASSET_VALUE_COLUMN,
        PERFORMANCE_1_MONTH_COLUMN,
        PERFORMANCE_1_YEAR_COLUMN,
        HOLDING_ADDRESS_COUNT_COLUMN,
        {
            ...UNDERLYING_ASSET_COLUMN,
            label: (
                <>
                    Underlying
                    <br />
                    Security
                </>
            ),
            width: 150,
        },
        TOKENIZATION_TYPE_COLUMN,
        PLATFORM_COLUMN,
        { ...NETWORKS_COLUMN, sortsOn: AssetColumns.networkNames },
        BRIDGED_TOKEN_VALUE_DOLLAR_SPARKLINE_COLUMN,
        INCORPORATION_COUNTRY_COLUMN,
        LICENSE_OR_REGULATORY_FRAMEWORK,
        PRIMARY_MARKET_SUBSCRIPTION_FEE_COLUMN,
        PRIMARY_MARKET_REDEMPTION_FEE_COLUMN,
        TRAILING_30_DAY_TRANSFER_VOLUME_COLUMN,
        TRAILING_30_DAY_TRANSFER_COUNT_COLUMN,
        TRAILING_30_DAY_ACTIVE_ADDRESSES_COUNT_COLUMN,
        CONTRACT_ADDRESS_COLUMN,
    ];

// // // //

const STOCKS_ASSETS_QUERY = {
    filter: ASSET_CLASS_PAGES_TO_FILTER.stocks,
    sort: ASSET_CLASS_PAGE_TO_SORT.stocks,
    pagination: {
        page: 1,
        perPage: 25,
    },
};

/** Option values for the chart's "Underlying Type" segment select. */
enum UnderlyingTypeSegments {
    all = "all",
    fund = "etf_etp",
    equity = "equity",
}

/**
 * "Underlying Type" chart control — lets Asset Managers isolate the growth
 * trajectory of tokenized funds (ETFs/ETPs) vs. single stocks. The filters use
 * the VIRTUAL pegged_asset_type timeseries column, resolved server-side into
 * asset_id filters (see resolvePeggedAssetTypeFilters). Assets with no
 * pegged_asset_type recorded (a few hundred) match neither segment.
 */
const UNDERLYING_TYPE_SEGMENT_SELECT: TimeseriesSegmentSelectProps = {
    label: "Underlying Type",
    defaultValue: UnderlyingTypeSegments.all,
    options: [
        {
            label: "ETF/ETP",
            value: UnderlyingTypeSegments.fund,
            tooltip: "Tokenized funds — exchange-traded funds and products",
            loginRequired: true,
            filters: [
                {
                    field: TokenTimeseriesColumns.peggedAssetType,
                    operator: FilterOperators.In,
                    value: [ASSET_TYPES.ETF, ASSET_TYPES.ETP],
                },
            ],
        },
        {
            label: "Equity/Stock",
            value: UnderlyingTypeSegments.equity,
            tooltip: "Tokenized shares of individual companies",
            loginRequired: true,
            filters: [
                {
                    field: TokenTimeseriesColumns.peggedAssetType,
                    operator: FilterOperators.Equals,
                    value: ASSET_TYPES.Equity,
                },
            ],
        },
        {
            label: "All",
            value: UnderlyingTypeSegments.all,
            tooltip: "All tokenized stocks, funds, and products",
            filters: [],
        },
    ],
};

const FUND_ASSET_TYPES: string[] = [ASSET_TYPES.ETF, ASSET_TYPES.ETP];

/**
 * When the chart is grouped by Underlying Type, the API returns separate ETF
 * and ETP series. Merge them into a single "ETF/ETP" series (and relabel
 * EQUITY to "Equity/Stock") so the chart series match the segments of the
 * "Underlying Type" filter above the chart. No-op for every other grouping.
 */
function mergeUnderlyingTypeSeries(data: TimeSeriesItem[]): TimeSeriesItem[] {
    const isUnderlyingTypeGroup = (item: TimeSeriesItem) =>
        item.group.type === TokenTimeseriesGroupBy.peggedAssetType;

    const isFundSeries = (item: TimeSeriesItem) =>
        isUnderlyingTypeGroup(item) &&
        FUND_ASSET_TYPES.includes(item.group.name);

    const fundSeries = data.filter(isFundSeries);
    if (fundSeries.length === 0) {
        return data;
    }

    const totalsByDate = new Map<string, number>();
    fundSeries.forEach((series) => {
        series.points.forEach(([date, value]) => {
            totalsByDate.set(date, (totalsByDate.get(date) ?? 0) + value);
        });
    });

    const mergedFundSeries: TimeSeriesItem = {
        ...fundSeries[0],
        group: {
            ...fundSeries[0].group,
            name: "ETF/ETP",
        },
        points: [...totalsByDate.entries()].sort(([dateA], [dateB]) =>
            dateA.localeCompare(dateB),
        ),
    };

    // Emit the merged series in place of the first fund series, drop the rest
    let mergedEmitted = false;
    return data.flatMap((item) => {
        if (isFundSeries(item)) {
            if (mergedEmitted) {
                return [];
            }
            mergedEmitted = true;
            return [mergedFundSeries];
        }
        if (
            isUnderlyingTypeGroup(item) &&
            item.group.name === ASSET_TYPES.Equity
        ) {
            return [
                { ...item, group: { ...item.group, name: "Equity/Stock" } },
            ];
        }
        return [item];
    });
}

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// revalidation is enabled and a new request comes in
// NOTE - this will have to be changed to hit api.rwa.xyz when the API has been updated
export async function getStaticProps() {
    // Base filters for all public equity queries
    const baseFilters: Filter[] = [
        {
            field: TokenTimeseriesColumns.assetClassID,
            operator: FilterOperators.Equals,
            value: ASSET_CLASS_IDS.Stocks,
        },
    ];

    // Make all requests in parallel + destructure responses
    const [
        listQueryResponse,
        filterMetadata,
        leagueTableTabs,
        distributedAggregates,
        representedAggregates,
        transferVolumeData,
        activeAddressesData,
        holdersData,
    ] = await Promise.all([
        queryAssets({
            query: STOCKS_ASSETS_QUERY,
        }),
        queryAssetFilterMeta({
            filter: ASSET_CLASS_PAGES_TO_FILTER.stocks,
        }),
        queryLeagueTablesByDistributionType({
            name: "asset-class--stocks",
            filter: {
                field: TokenTimeseriesColumns.assetClassID,
                operator: FilterOperators.Equals,
                value: ASSET_CLASS_IDS.Stocks,
            },
            groups: [
                LeagueTableGroupTypes.platforms,
                LeagueTableGroupTypes.peggedAssets,
                LeagueTableGroupTypes.networks,
            ],
            labelOverrides: {
                [LeagueTableGroupTypes.peggedAssets]: {
                    key: "underlying_stock",
                    label: "Underlying Stock",
                    groupColumnLabel: "Stock",
                    enableEntityLinks: false,
                },
            },
        }),
        // 1. Distributed Asset Value
        queryTokenAggregateValueWith30D({
            name: "asset-class--stocks--distributed-asset-value",
            measureID: MEASURE_IDS.BridgedTokenValueDollar,
            filters: [
                ...baseFilters,
                {
                    field: TokenTimeseriesColumns.tokenizationTypeID,
                    operator: FilterOperators.Equals,
                    value: TOKENIZATION_TYPE_IDS.OnChainDistributed,
                },
            ],
        }),
        // 2. Represented Asset Value
        queryTokenAggregateValueWith30D({
            name: "asset-class--stocks--represented-asset-value",
            measureID: MEASURE_IDS.BridgedTokenValueDollar,
            filters: [
                ...baseFilters,
                {
                    field: TokenTimeseriesColumns.tokenizationTypeID,
                    operator: FilterOperators.Equals,
                    value: TOKENIZATION_TYPE_IDS.OnChainRepresented,
                },
            ],
        }),

        queryTokenAggregateValueWith30D({
            name: "asset-class--stocks--monthly-transfer-volume",
            measureID: MEASURE_IDS.Trailing30DayTransferVolumeDollar,
            filters: baseFilters,
        }),
        queryTokenAggregateValueWith30D({
            name: "asset-class--stocks--monthly-active-addresses",
            measureID: MEASURE_IDS.MonthlyActiveAddresses,
            filters: baseFilters,
        }),
        queryTokenAggregateValueWith30D({
            name: "asset-class--stocks--holders",
            measureID: MEASURE_IDS.DailyNumberOfHolders,
            filters: baseFilters,
        }),
    ]);

    const aggregates: Array<AggregateDisplayProps> = [
        {
            label: "Distributed Value",
            value: distributedAggregates.value,
            type: AggregateValueTypes.dollarCompact,
            tooltip: DISTRIBUTION_TYPE_TOOLTIPS.distributed,
            tooltipLinkTo: DISTRIBUTION_TYPE_AGGREGATE_TOOLTIP_LINK_TO,
            percentChange: {
                value: distributedAggregates.change30D,
                interval: "30d",
            },
        },
        {
            label: "Represented Value",
            value: representedAggregates.value,
            type: AggregateValueTypes.dollarCompact,
            tooltip: DISTRIBUTION_TYPE_TOOLTIPS.represented,
            tooltipLinkTo: DISTRIBUTION_TYPE_AGGREGATE_TOOLTIP_LINK_TO,
            percentChange: {
                value: representedAggregates.change30D,
                interval: "30d",
            },
        },
        {
            label: "Monthly Transfer Volume",
            tooltip:
                "Sum of value transferred across all tracked tokenized stocks in the last 30 days",
            type: AggregateValueTypes.dollarCompact,
            value: transferVolumeData.value,
            percentChange: {
                value: transferVolumeData.change30D,
                interval: "30d",
            },
        },
        {
            label: "Monthly Active Addresses",
            tooltip:
                "Count of addresses that have transferred a tracked tokenized stock in the 30 days",
            type: AggregateValueTypes.count,
            value: activeAddressesData.value,
            percentChange: {
                value: activeAddressesData.change30D,
                interval: "30d",
            },
        },
        {
            label: "Holders",
            tooltip: "The number of addresses that hold tokenized stocks",
            type: AggregateValueTypes.countCompact,
            value: holdersData.value,
            percentChange: {
                value: holdersData.change30D,
                interval: "30d",
            },
        },
    ];

    // Trim down FilterMetadata options to only include the ones we need,
    // re-label some of the options for clarity + consistency
    const trimmedFilterMetadata = filterMetadata
        .filter((f) => {
            return [
                SEARCH_FILTER_METADATA.field,
                AssetColumns.protocolIDs,
                AssetColumns.networkIDs,
                AssetColumns.peggedAssetType,
                AssetColumns.tokenizationTypeIDs,
            ].includes(f.field as AssetColumns);
        })
        .map((f) => {
            if (f.field === AssetColumns.peggedAssetType) {
                return {
                    ...f,
                    content: {
                        ...f.content,
                        label: "Underlying Asset Type",
                    },
                };
            }

            if (f.field === AssetColumns.tokenizationTypeIDs) {
                return {
                    ...f,
                    content: {
                        ...f.content,
                        label: "Tokenization Type",
                    },
                };
            }

            return f;
        });

    return {
        props: {
            listQueryResponse,
            filterMetadata: trimmedFilterMetadata,
            leagueTableTabs,
            aggregates,
        },
        // Next.js will re-generate this page based on NEXTJS_REVALIDATE_TIMEOUT const
        revalidate: NEXTJS_REVALIDATE_TIMEOUT,
    };
}

export default function StocksPage(props: {
    aggregates: Array<AggregateDisplayProps>;
    listQueryResponse: QueryResponse<Asset>;
    filterMetadata: FilterMetadata[];
    leagueTableTabs: LeagueTableTabsByDistributionType;
}) {
    const { aggregates, listQueryResponse, leagueTableTabs, filterMetadata } =
        props;

    const modes = getTimeseriesModes({
        analyticsKey: "stocks",
        defaultGroupBy: TokenTimeseriesGroupBy.asset,
        measureSlug: MEASURE_SLUGS.bridged_token_value_dollar,
        filters: ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS.stocks,
        groupByOptions: [
            TokenTimeseriesGroupBy.asset,
            TokenTimeseriesGroupBy.token,
            TokenTimeseriesGroupBy.peggedAssetType,
            TokenTimeseriesGroupBy.network,
            TokenTimeseriesGroupBy.protocol,
        ],
        groupByOptionsWithNewIndicator: [
            TokenTimeseriesGroupBy.peggedAssetType,
        ],
        groupByOptionsLabelOverrides: {
            [TokenTimeseriesGroupBy.peggedAssetType]: "Underlying Type",
        },
    });

    return (
        <PageTrack route={Routes.stocks}>
            <NextSeo
                title={SEO_TITLE}
                description={SEO_DESCRIPTION}
                canonical={addBaseUrl(Routes.stocks)}
                openGraph={{
                    type: "website",
                    url: addBaseUrl(Routes.stocks),
                    title: SEO_TITLE,
                    site_name: "RWA.xyz",
                    description: SEO_DESCRIPTION,
                    images: [
                        {
                            url: SOCIAL_OG_IMAGE_URL,
                            width: 4000,
                            height: 2300,
                            alt: "rwa.xyz",
                        },
                    ],
                }}
                additionalMetaTags={[
                    {
                        name: "keywords",
                        content:
                            "tokenized stocks, tokenized equities, stock tokens, digital securities, tokenized shares, tokenized ETFs, real world assets",
                    },
                ]}
            />
            <AssetClassPageSEO
                route={Routes.stocks}
                description={SEO_DESCRIPTION}
            />
            <BreadcrumbSEO
                items={[
                    {
                        name: SEO_TITLE,
                        href: Routes.stocks,
                    },
                ]}
            />
            <UserFetcher>
                {({ user }) => <Navbar route={Routes.stocks} user={user} />}
            </UserFetcher>
            <SidebarLayout pathname={Routes.stocks}>
                <PageWrapper>
                    <AssetClassIndividualPage
                        listQueryResponse={listQueryResponse}
                        title={SEO_TITLE}
                        subtitle={SEO_DESCRIPTION}
                        assetsQuery={STOCKS_ASSETS_QUERY}
                        header={{
                            label: "Tokenized Stocks",
                            loading: false,
                            value: `${listQueryResponse.pagination.resultCount.toLocaleString()} Total`,
                        }}
                        aggregates={aggregates}
                        timeseriesChart={{
                            title: "Tokenized Stock Metrics",
                            modes,
                            segmentSelect: UNDERLYING_TYPE_SEGMENT_SELECT,
                            transformTimeseriesData: mergeUnderlyingTypeSeries,
                            // NOTE - no distributionSelect: represented value
                            // is ~1% of tokenized stocks, so the Asset Type
                            // control isn't worth the control-bar space here
                            columnSpanConfig: {
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 12,
                                xxl: 12,
                            },
                        }}
                        pieChart={{
                            leagueTableData: leagueTableTabs,
                            title: "Tokenized Stocks Value",
                            defaultGroupBy: LeagueTableGroupTypes.platforms,
                            allowedGroupByValues: [
                                LeagueTableGroupTypes.platforms,
                                LeagueTableGroupTypes.peggedAssets,
                                LeagueTableGroupTypes.networks,
                            ],
                            groupByLabelOverrides: {
                                [LeagueTableGroupTypes.peggedAssets]:
                                    "Underlying Stock",
                            },
                            colSpans: {
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 6,
                                xxl: 6,
                            },
                        }}
                        leagueTable={{
                            leagueTableData: leagueTableTabs,
                            analyticsTableName: "stocks-league-table",
                            title: "Tokenized Stocks League Table",
                            colSpans: {
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 6,
                                xxl: 6,
                            },
                        }}
                        table={{
                            columns: STOCKS_TABLE_COLUMNS,
                            filterMetadata,
                            analyticsTableName: "public_equity",
                            exportFilename: "stocks-assets",
                            sortQuery: {
                                field: AssetColumns.bridgedTokenValueDollar,
                                direction: SortDirections.desc,
                            },
                        }}
                    />
                    <Footer />
                </PageWrapper>
            </SidebarLayout>
        </PageTrack>
    );
}
